/**
 * API 根地址
 * - 生产：VUE_APP_API_BASE_URL 绝对地址
 * - 本地开发：axios 走相对路径 /api，由 vue.config.js devServer 代理到远程
 * - 登录页「环境切换」写入 localStorage + Cookie，代理按 Cookie 动态转发
 */
import devApiEnvs from './dev-api-envs.json'

const ENV_STORAGE_KEY = 'laundry_dev_api_env'
const ENV_COOKIE_KEY = 'laundry_dev_api_env'
const IS_DEV = process.env.NODE_ENV !== 'production'

/** 本地开发可选环境（与 dev-api-envs.json 保持一致） */
export const DEV_API_ENVS = devApiEnvs

const envBaseURL = process.env.VUE_APP_API_BASE_URL
const envDeployName = process.env.VUE_APP_DEPLOY_NAME || '默认'

function readDevEnvKey() {
  if (typeof window === 'undefined') return ''
  if (!IS_DEV) return ''
  try {
    return localStorage.getItem(ENV_STORAGE_KEY) || ''
  } catch (e) {
    return ''
  }
}

function syncDevEnvCookie(key) {
  if (typeof document === 'undefined' || !IS_DEV) return
  if (key) {
    document.cookie = `${ENV_COOKIE_KEY}=${encodeURIComponent(key)}; path=/; max-age=31536000`
  } else {
    document.cookie = `${ENV_COOKIE_KEY}=; path=/; max-age=0`
  }
}

function resolveRemoteConfig() {
  const key = readDevEnvKey()
  if (key) {
    const found = DEV_API_ENVS.find((item) => item.key === key)
    if (found) {
      return { remoteBaseURL: found.baseURL, deployName: found.name, key: found.key }
    }
  }
  return {
    remoteBaseURL: envBaseURL || 'https://www.xiquesoftware.com/manage',
    deployName: envDeployName,
    key: '',
  }
}

const resolved = resolveRemoteConfig()

if (!envBaseURL && !resolved.key && IS_DEV) {
  console.warn(
    '[API] VUE_APP_API_BASE_URL 未配置，请检查 .env.development 或使用登录页环境切换',
  )
}

/** axios baseURL：开发为空（走 devServer 代理），生产为远程根地址 */
export const API_BASE_URL = IS_DEV ? '' : resolved.remoteBaseURL
/** 实际后端根地址（用于展示、上传绝对 URL、资源 origin） */
export const REMOTE_API_BASE_URL = resolved.remoteBaseURL
export const DEPLOY_NAME = resolved.deployName
export const CURRENT_DEV_ENV_KEY = resolved.key

if (IS_DEV) {
  syncDevEnvCookie(resolved.key || readDevEnvKey())
}

/** 开发环境切换 API（写入后建议清登录态并刷新） */
export function setDevApiEnv(key) {
  if (!IS_DEV) {
    console.warn('[API] 正式环境不允许切换 API')
    return false
  }
  if (!key) {
    localStorage.removeItem(ENV_STORAGE_KEY)
    syncDevEnvCookie('')
    return true
  }
  const found = DEV_API_ENVS.find((item) => item.key === key)
  if (!found) {
    console.warn('[API] 未知环境:', key)
    return false
  }
  localStorage.setItem(ENV_STORAGE_KEY, key)
  syncDevEnvCookie(key)
  return true
}

/** 拼接 API 地址，用于 el-upload action 等需完整 URL 的场景 */
export function buildApiUrl(path) {
  const normalized = path.startsWith('/') ? path : `/${path}`
  if (IS_DEV) {
    return normalized
  }
  return `${REMOTE_API_BASE_URL}${normalized}`
}

/** 从远程 API 根地址解析站点 origin */
export function getRemoteOrigin() {
  try {
    return new URL(REMOTE_API_BASE_URL).origin
  } catch {
    return 'https://www.xiquesoftware.com'
  }
}

/** 从 API 根地址解析站点 origin（开发环境为当前页面 origin） */
export function getAppOrigin() {
  if (IS_DEV && typeof window !== 'undefined' && window.location) {
    return window.location.origin
  }
  return getRemoteOrigin()
}

/**
 * 将面单/资源 URL 转为当前部署环境可访问的完整地址。
 * - 含 /electron：拼到当前站点 origin
 * - 快递鸟域名：本地走 /pdf-proxy，避免 CORS
 * - 已是绝对 http(s)：原样返回（非快递鸟）
 * - 相对路径 /xxx：拼 origin
 * - 纯文件名 xxx.pdf：挂到 /electron/ 下
 */
export function buildElectronResourceUrl(url) {
  if (!url) return ''
  let raw = String(url).trim().replace(/&amp;/g, '&')
  if (raw.startsWith('//')) {
    raw = `https:${raw}`
  }

  // 快递鸟资源：开发环境走 vue 代理
  if (/resources\.kdniao\.com|api\.kdniao\.com/i.test(raw)) {
    try {
      const parsed = new URL(raw)
      return `/pdf-proxy${parsed.pathname}${parsed.search}`
    } catch {
      return raw
    }
  }

  // 本站 electron 面单
  const electronMatch = raw.match(/\/electron[^\s"'?]*/i)
  if (electronMatch) {
    return `${getRemoteOrigin()}${electronMatch[0]}`
  }

  if (/^https?:\/\//i.test(raw)) {
    return raw
  }

  if (raw.startsWith('/')) {
    return `${getRemoteOrigin()}${raw}`
  }

  if (/\.pdf$/i.test(raw) && !raw.includes('/')) {
    return `${getRemoteOrigin()}/electron/${raw}`
  }

  return ''
}
